import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';


interface AuthContextType {
  email: string | null;
  token: string | null;
  login: (token: string, email: string, userProps?: Record<string, any>) => void;
  logout: () => void;
  isTokenExpired: () => boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const isTokenExpiredFunc = (token: string | null): boolean => {
  if (!token) {
      return true;
  }

  try {
    const decodedToken: { exp: number } = jwtDecode<{ exp: number }>(token);
    const currentTime = Date.now() / 1000;
    return decodedToken.exp < currentTime;
  } catch (error) {
    return true;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [email, setEmail] = useState<string | null>(localStorage.getItem('email'));
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const navigate = useNavigate();

    // login 逻辑
  const login = useCallback((newToken: string, newEmail: string, userProps?: Record<string, any>) => {
      if (isTokenExpiredFunc(newToken)) {
        logout();
        return;
      }
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('email', newEmail);
    setToken(newToken);
    setEmail(newEmail);
    // 埋点：标记用户身份
    try {
      if (newEmail) {
        if (userProps) {
          posthog.identify(newEmail, userProps);
        } else {
          posthog.identify(newEmail);
        }
      }
    } catch (error) {
      // 埋点：identify 失败
      if (newEmail) {
        import('../../../utils/analytics').then(m => m.trackError?.('identify_error', String(error), 'AuthContext.login', { email: newEmail }));
      }
    }
    navigate('/home');
  }, [navigate]);

  // logout 逻辑
  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('email');
    setToken(null);
    setEmail(null);
    // 埋点：清除用户身份
    posthog.reset();
    navigate('/login');
  }, [navigate]);

    // token 过期检查逻辑
  useEffect(() => {
    if (!token) return;

    // 定时器定期检查
  const intervalId = setInterval(() => {
    if (isTokenExpiredFunc(token)) {
      logout();
    }
  }, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [token, logout]);

    // 用于暴露给 context 的 isTokenExpired
  const isTokenExpired = useCallback(() => isTokenExpiredFunc(token), [token]);

  // 用 useMemo 保持 contextValue 引用稳定，避免无意义 re-render
  const contextValue = useMemo(() => ({
    email,
    token,
    login,
    logout,
    isTokenExpired,
  }), [email, token, login, logout, isTokenExpired]);


  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }
  return context;
};
